import { createFileRoute, useParams, useNavigate } from '@tanstack/react-router'
import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { List } from 'lucide-react'
import { useSession } from '@/lib/auth'
import { apiFetch, apiFetchJson } from '@/lib/api'
import { TaskListView } from '@/components/features/tasks/views/TaskListView'
import { ConfirmDeleteModal } from '@/components/common/ConfirmDeleteModal'

export interface MyTasksSearch {
  userId?: string
}

export const Route = createFileRoute('/$workspaceSlug/my-tasks/')({
  component: MyTasksPage,
  validateSearch: (search: Record<string, unknown>): MyTasksSearch => {
    return {
      userId: search.userId as string | undefined,
    }
  },
})

function MyTasksPage() {
  const { t } = useTranslation()
  const { workspaceSlug } = useParams({ strict: false }) as { workspaceSlug: string }
  const navigate = useNavigate()
  const { data: session } = useSession()
  const queryClient = useQueryClient()
  const searchParams = Route.useSearch()

  const [deleteTaskId, setDeleteTaskId] = useState<string | null>(null)
  const [selectedTasks, setSelectedTasks] = useState<string[]>([])

  // Fetch tasks
  const { data: tasksData, isLoading: isLoadingTasks } = useQuery({
    queryKey: ['workspace-tasks', workspaceSlug],
    queryFn: () => apiFetchJson<any>(`/api/tasks?workspaceSlug=${workspaceSlug}`),
    enabled: !!workspaceSlug && !!session?.user?.id,
  })

  // Fetch projects to map project names and stages (statuses)
  const { data: projectsData, isLoading: isLoadingProjects } = useQuery({
    queryKey: ['workspace-projects', workspaceSlug],
    queryFn: () => apiFetchJson<any>(`/api/projects?workspaceSlug=${workspaceSlug}`),
    enabled: !!workspaceSlug,
  })

  const allTasks = tasksData?.data || []
  const allProjects = projectsData?.data || []

  const targetUserId = searchParams.userId || session?.user?.id

  const assignedTasks = useMemo(() => {
    if (!targetUserId) return []
    return allTasks.filter((task: any) => {
      const isAssigned =
        task.assignees?.some((a: any) => a === targetUserId || a.id === targetUserId) ||
        task.assigneeDetails?.some((a: any) => a.id === targetUserId)
      return isAssigned
    })
  }, [allTasks, targetUserId])

  const allStages = useMemo(() => {
    const stages: any[] = []
    allProjects.forEach((p: any) => {
      if (p.stages) {
        stages.push(...p.stages)
      }
    })
    return stages
  }, [allProjects])

  const listViewTasks = useMemo(() => {
    return assignedTasks.map((t: any) => {
      const proj = allProjects.find((p: any) => p.id === t.projectId)
      return {
        id: t.id,
        title: t.title,
        description: t.description || '',
        priority: (t.priority || 'medium') as 'urgent' | 'high' | 'medium' | 'low',
        assigneeDetails: (t.assigneeDetails ||
          (Array.isArray(t.assignees) && typeof t.assignees[0] === 'object'
            ? t.assignees
            : [])) as any[],
        status: t.status,
        dueDate: t.dueDate || undefined,
        startDate: t.startDate,
        endDate: t.endDate,
        subtaskCount: t.subtasksCount || 0,
        subtaskCompleted: t.subtasksCompleted || 0,
        commentCount: t.commentsCount || 0,
        attachmentCount: t.attachmentCount || 0,
        labels: t.labels || [],
        type: 'task' as const,
        dependsOn: t.dependsOn || [],
        isCompleted: t.isCompleted || false,
        projectName: proj?.name || 'Unknown Project',
      }
    })
  }, [assignedTasks, allProjects])

  const loading = isLoadingTasks || isLoadingProjects

  const handleTaskSelect = (taskId: string, selected: boolean) => {
    setSelectedTasks((prev) => (selected ? [...prev, taskId] : prev.filter((id) => id !== taskId)))
  }

  const handleSelectAll = (selected: boolean) => {
    setSelectedTasks(selected ? listViewTasks.map((t: any) => t.id) : [])
  }

  const handleDeleteTask = async () => {
    if (!deleteTaskId) return
    try {
      await apiFetch(`/api/tasks/${deleteTaskId}`, {
        method: 'DELETE',
        headers: { 'x-user-id': session?.user?.id || '' },
      })
      queryClient.invalidateQueries({ queryKey: ['workspace-tasks', workspaceSlug] })
    } catch (error) {
      console.error('Failed to delete task', error)
    }
    setDeleteTaskId(null)
    setSelectedTasks((prev) => prev.filter((id) => id !== deleteTaskId))
  }

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center p-8 text-white">
        <div className="text-gray-500">{t('common.loading')}</div>
      </div>
    )
  }

  return (
    <div className="flex h-full flex-1 flex-col overflow-hidden p-8 text-white">
      <header className="mb-6 flex shrink-0 flex-col gap-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold">
              {searchParams.userId
                ? t('dashboard.membersTasks', { defaultValue: "Member's Tasks" })
                : t('dashboard.myTasks')}
            </h1>
          </div>
        </div>
      </header>

      <div className="-mx-8 flex-1 overflow-auto px-8">
        {listViewTasks.length === 0 ? (
          <div className="mt-12 flex h-64 flex-col items-center justify-center rounded-2xl border border-[var(--app-border)] bg-[var(--app-bg-card)] p-8 text-gray-500">
            <List className="mb-4 h-12 w-12 text-gray-400 opacity-50" />
            <p className="text-lg font-medium text-gray-300">{t('dashboard.myTasks')}</p>
            <p className="text-sm">{t('dashboard.noFiles') || 'No tasks assigned to you.'}</p>
          </div>
        ) : (
          <TaskListView
            tasks={listViewTasks}
            columns={allStages.map((s: any) => ({
              id: s.id,
              title: s.name,
              color: s.color || '#6c6c6c',
            }))}
            onTaskClick={(id) =>
              navigate({
                to: `/${workspaceSlug}/projects/${listViewTasks.find((t: any) => t.id === id)?.projectId}?taskId=${id}`,
              })
            }
            selectedTasks={selectedTasks}
            onTaskSelect={handleTaskSelect}
            onSelectAll={handleSelectAll}
            onTaskEdit={(id) =>
              navigate({
                to: `/${workspaceSlug}/projects/${listViewTasks.find((t: any) => t.id === id)?.projectId}?taskId=${id}`,
              })
            }
            onTaskDelete={(id) => setDeleteTaskId(id)}
            userId={session?.user?.id}
          />
        )}
      </div>

      {/* Confirm Delete Modal */}
      <ConfirmDeleteModal
        isOpen={!!deleteTaskId}
        onClose={() => setDeleteTaskId(null)}
        onConfirm={handleDeleteTask}
        title={t('common.delete')}
        message={t('tasks.details.delete')}
      />
    </div>
  )
}
